import { HttpMethod } from "@/lib/utils/http-methods";
import { StringHelper } from "@bryntum/core-thin";
import type { ResourceTimeRangeModel } from "@bryntum/scheduler-thin";
import type { EventModel, SchedulerPro } from "@bryntum/schedulerpro-thin";
import { getAttachedDate, getDetachedDay } from "@laborchart-modules/common/dist/datetime";
import type { SchedulerCalendarInterval } from "@laborchart-modules/lc-core-api/dist/api/assignments/load-resource";
import type { useI18nContext } from "@procore/core-react";
import { formatTimeBryntum, getDatesRangeString } from "../gantt-container";
import { getTimeOffReasonDisplay, timeFloatToString } from "../gantt-utils";
import { TaskType } from "../prop-types";
import type { WorkDays } from "@laborchart-modules/common/dist/postgres/schemas/common/custom-json";
import type { UnassignTaskData } from "../gantt-unassign-handler";
import { Warning } from "@procore/core-icons";
import { createElement } from "react";
import { renderToString } from "react-dom/server";
import { getEventRecordData } from "./resource-schedule-helpers";
import type { ResourceModel } from "@bryntum/scheduler-thin";
import type {
   BeforeDragCreateFinalizeParams,
   DragCreateAssignmentParams,
   DragCreateContext,
   DragResizeRecord,
   EventRecordForUnassign,
   EventTooltipProps,
   NewTaskFromTearsheet,
   SchedulerEvent,
   TimeOffTooltipData,
   DragResizeRecordKey,
} from "./resource-schedule-types";
import { ResourceType } from "@/react/prop-types";
import { NonWorkingDayWarning } from "./resource-schedule-types";

/**
 * Gets the task time allocation display string
 * Returns time range if both start/end times exist, otherwise returns percentage allocation
 */
export function getTaskTimeAllocation(
   startTime: string | null,
   endTime: string | null,
   percentAllocated: number | null,
): string {
   if (!startTime || !endTime) {
      return percentAllocated !== null ? `${percentAllocated}` : "";
   }
   return `${startTime} - ${endTime}`;
}

export function buildDragResizeTooltip(
   eventRecord: DragResizeRecord,
   startDate: Date,
   endDate: Date,
   I18n?: ReturnType<typeof useI18nContext>,
): string {
   const { startTime, endTime, percentAllocated } = eventRecord;
   const eventStartTime = startTime ? formatTimeBryntum(String(startTime)) : "";
   const eventEndTime = endTime ? formatTimeBryntum(String(endTime)) : "";
   const taskTimeAllocation = getTaskTimeAllocation(eventStartTime, eventEndTime, percentAllocated);
   const isTaskWithTime = !!(startTime && endTime);
   const dateRange = StringHelper.encodeHtml(getDatesRangeString(startDate, endDate));

   let allocationHtml = "";
   if (taskTimeAllocation) {
      const display = isTaskWithTime ? taskTimeAllocation : `${taskTimeAllocation}%`;
      allocationHtml = `<div style="text-align: center">${StringHelper.encodeHtml(display)}</div>`;
   }

   let warningHtml = "";
   const { workDays } = eventRecord;
   if (I18n && workDays) {
      const startOk = workDays[startDate.getDay()];
      const endOk = workDays[new Date(endDate.getTime() - 1).getDay()];
      let key: NonWorkingDayWarning | null = null;
      if (!startOk && !endOk) key = NonWorkingDayWarning.Both;
      else if (!startOk) key = NonWorkingDayWarning.Start;
      else if (!endOk) key = NonWorkingDayWarning.End;
      if (key) {
         const message = I18n.t(
            `views.company.workforce_planning.assignment.drag_tooltip_non_working_${key}`,
         );
         const warningIcon = renderToString(createElement(Warning, { size: "sm" }));
         warningHtml = `<div class="scheduler-drag-tooltip-warning">${warningIcon} ${StringHelper.encodeHtml(
            message,
         )}</div>`;
      }
   }

   return `<div>
      <div style="text-align: center">${dateRange}</div>
      ${allocationHtml}
      ${warningHtml}
   </div>`;
}

export function getDragResizeFeatureConfig(
   eventRecordKey: DragResizeRecordKey,
   I18n?: ReturnType<typeof useI18nContext>,
) {
   return {
      tooltipTemplate(data: { startDate: Date; endDate: Date } & Record<string, DragResizeRecord>) {
         // Bryntum rounds endDate up to midnight of the next day; subtract one minute to display the correct day
         data.endDate.setMinutes(data.endDate.getMinutes() - 1);
         return buildDragResizeTooltip(data[eventRecordKey], data.startDate, data.endDate, I18n);
      },
      tip: {
         htmlCls: "taskDragTooltipWrapper",
         bodyCls: "taskDragTooltip",
      },
   };
}

export function getEventDragCreateFeatureConfig(canManageAssignments: boolean) {
   if (!canManageAssignments) return false;
   return {
      tooltipTemplate({ startDate, endDate }: { startDate: Date; endDate: Date }) {
         endDate.setMinutes(endDate.getMinutes() - 1);
         const dateRange = StringHelper.encodeHtml(getDatesRangeString(startDate, endDate));
         return `<div style="text-align: center">${dateRange}</div>`;
      },
      tip: {
         htmlCls: "taskDragTooltipWrapper",
         bodyCls: "taskDragTooltip",
      },
   };
}

export function getBeforeDragCreateFinalizeHandler({
   dragCreateContextRef,
   createAssignmentTearsheetRef,
   schedulerRef,
   handleDragCreate,
}: BeforeDragCreateFinalizeParams) {
   return ({
      eventRecord: phantomEventRecord,
      resourceRecord,
      context,
   }: {
      eventRecord: EventModel;
      resourceRecord: ResourceModel;
      context: DragCreateContext & { startDate: Date; endDate: Date; async: boolean };
   }) => {
      // TODO(WFP-6930): Remove equipment guard once equipment assignment creation is supported via drag create.
      if (resourceRecord.getData("resourceType") === ResourceType.EQUIPMENT) {
         context.finalize(false);
         return;
      }

      context.async = true;

      const startDate = context.startDate;
      const endDate = new Date(context.endDate);
      endDate.setMinutes(endDate.getMinutes() - 1);
      const resourceId = resourceRecord.getData("resourceId");
      const resourceName = resourceRecord.getData("name");

      dragCreateContextRef.current = {
         context,
         resourceRecord,
         phantomEventRecord,
      };

      createAssignmentTearsheetRef?.current?.handleOpenTearsheet({
         projectId: "",
         startDate,
         endDate,
         people: [{ id: resourceId, label: resourceName }],
         callback: async ({ newTask }: { newTask: DragCreateAssignmentParams["newTask"] }) => {
            const saved = dragCreateContextRef.current;
            if (!saved) return;

            const isFirstPerson = !saved.finalized;
            saved.finalized = true;

            await handleDragCreate({
               ...(isFirstPerson && { dragCreateContext: saved.context }),
               newTask,
               schedulerRef,
            });
         },
         onClose: async () => {
            const saved = dragCreateContextRef.current;
            dragCreateContextRef.current = null;
            if (!saved || saved.finalized) return;

            const scheduler = schedulerRef.current?.instance;
            if (!scheduler?.project) return;

            const project = scheduler.project as any;
            if (project?.suspendAutoSync) await project.suspendAutoSync();

            saved.context.finalize(false);

            if (saved.phantomEventRecord) project.eventStore.remove(saved.phantomEventRecord);

            const crud = (scheduler as any).crudManager;
            if (crud?.clearChanges) await crud.clearChanges();

            if (project?.resumeAutoSync) await project.resumeAutoSync();
         },
      });
   };
}

/**
 * Prepares tooltip props from event record data
 * This function is extracted to make it testable and reusable
 */
export function prepareEventTooltipProps(
   eventRecord: any,
   paidShiftHours: number,
): EventTooltipProps {
   const {
      projectName,
      projectJobNumber,
      startDate,
      endDate,
      workDays,
      jobTitleName,
      jobTitleColor,
      resourceName,
      startTime,
      endTime,
      percentAllocated,
   } = eventRecord;

   const eventStartTime = startTime ? formatTimeBryntum(startTime) : "";
   const eventEndTime = endTime ? formatTimeBryntum(endTime) : "";
   const workingDayClass = (day: number) => (workDays?.[day] ? "bold" : "thin");
   const taskTimeAllocation = getTaskTimeAllocation(eventStartTime, eventEndTime, percentAllocated);

   return {
      type: TaskType.ASSIGNMENT,
      projectName,
      projectJobNumber,
      dateRangeString: getDatesRangeString(startDate, endDate),
      startTime: eventStartTime,
      endTime: eventEndTime,
      isTaskWithTime: !!(startTime && endTime),
      isEquipment: eventRecord.resourceType === ResourceType.EQUIPMENT,
      taskTimeAllocation,
      workingDayClass,
      jobTitleName,
      jobTitleColor,
      paidShiftHours,
      taskName: eventRecord.assignments?.[0]?.resource?.name ?? resourceName,
   };
}

/**
 * Converts a string date in the form "YYYY-MM-DD" to a Date object
 * or attempt to create a Date from the value
 * or returns the Date given
 * @param date the date to convert
 * @returns a Date
 */
const toLocalDate = (date: string | Date): Date => {
   if (typeof date === "string") {
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
      if (match) {
         const year = Number(match[1]);
         const month = Number(match[2]) - 1;
         const day = Number(match[3]);
         return new Date(year, month, day);
      }
      return new Date(date);
   }
   return date;
};

/**
 * Normalizes date/time fields by combining date and time values.
 * Works with both scheduler events and calendar intervals.
 * Ensures all assignments render as full bars in the scheduler by setting times to span
 * the entire day (00:00:00 - 23:59:59.999) when times are not specified (mainly for
 * allocation-based assignments), including for one-day assignments.
 * Mutates the records array in place for performance.
 */
export function normalizeDateTimeFields(
   records: SchedulerEvent[] | SchedulerCalendarInterval[],
): void {
   records.forEach((record) => {
      if (record.startDate) {
         const eventStartDate = toLocalDate(record.startDate);
         if (record.startTime) {
            const [hours = 0, minutes = 0, seconds = 0] = String(record.startTime)
               .split(":")
               .map(Number);
            eventStartDate.setHours(hours, minutes, seconds, 0);
         } else {
            eventStartDate.setHours(0, 0, 0, 0);
         }
         record.startDate = eventStartDate;
      }
      if (record.endDate) {
         const eventEndDate = toLocalDate(record.endDate);
         if (record.endTime) {
            const [hours = 0, minutes = 0, seconds = 0] = String(record.endTime)
               .split(":")
               .map(Number);
            eventEndDate.setHours(hours, minutes, seconds, 0);
         } else {
            eventEndDate.setHours(23, 59, 59, 999);
         }
         record.endDate = eventEndDate;
      }
   });
}

export function extractTimeOffTooltipData(
   rangeRecord: ResourceTimeRangeModel,
   scheduler: SchedulerPro,
   I18n: ReturnType<typeof useI18nContext>,
): TimeOffTooltipData {
   const startDate = new Date(rangeRecord.startDate);
   const endDate = new Date(rangeRecord.endDate);
   const intervalId = `interval-${rangeRecord.resourceId}-${
      startDate.toISOString().split("T")[0]
   }-${endDate.toISOString().split("T")[0]}`;

   const calendarId = `calendar-resource-${rangeRecord.resourceId}`;
   const calendar: any = scheduler.project.calendarManagerStore.getById(calendarId);
   const resource: any = scheduler.resourceStore.getById(rangeRecord.resourceId);
   // rangeRecord dates are clipped to minDate/maxDate when a date range is active,
   // so the constructed ID may not match the original backend interval ID.
   // Exact ID match first, then fall back to date containment.
   const customInterval =
      calendar?.intervals?.find((interval: any) => interval.id === intervalId) ??
      calendar?.intervals?.find((interval: any) => {
         const iStart = new Date(interval.startDate).getTime();
         const iEnd = new Date(interval.endDate).getTime();
         return startDate.getTime() >= iStart && endDate.getTime() <= iEnd;
      });
   const timeOffReason = getTimeOffReasonDisplay(customInterval?.reason, I18n);

   return {
      name: resource?.name || "",
      jobTitleName: resource?.jobTitle || "",
      jobTitleColor: resource?.jobTitleColor || "#666",
      timeOffReason,
      startDate,
      endDate,
      startTime: customInterval?.startTime || "",
      endTime: customInterval?.endTime || "",
      isPaid: customInterval?.isPaid || false,
      applyToSaturday: customInterval?.applyToSaturday || false,
      applyToSunday: customInterval?.applyToSunday || false,
      repeat: customInterval?.repeat || "",
   };
}

/**
 * Maps tearsheet callback newTask (gantt task shape) to scheduler event record shape
 * for in-place update. Preserves fields that identify the event (id, assignmentId, resourceName)
 * and updates date/time/display fields to match the edited assignment.
 */
export function mapNewTaskToSchedulerEvent(
   newTask: NewTaskFromTearsheet | null,
): Partial<SchedulerEvent> {
   if (!newTask) return {};
   const startDate = newTask.startDate ? new Date(newTask.startDate) : undefined;
   const endDate = newTask.endDate ? new Date(newTask.endDate) : undefined;
   const result: Partial<SchedulerEvent> = {
      projectId: newTask.projectId,
      projectName: newTask.projectName ?? newTask.name,
      projectJobNumber: newTask.projectJobNumber ?? newTask.jobNumber,
      projectColor: newTask.projectColor ?? null,
      percentAllocated: newTask.percentAllocated ?? null,
      workDays: newTask.workDays ?? null,
      jobTitleName: newTask.jobTitleName ?? null,
      jobTitleColor: newTask.jobTitleColor ?? null,
      overtime: newTask.overtime ?? false,
   };
   if (startDate) result.startDate = startDate;
   if (endDate) result.endDate = endDate;
   if (newTask.startTime != null) result.startTime = newTask.startTime;
   if (newTask.endTime != null) result.endTime = newTask.endTime;
   if (newTask.eventColor != null) result.eventColor = newTask.eventColor;
   const statusId = newTask.status?.id ?? newTask.statusId;
   const statusName = newTask.status?.name ?? newTask.statusName;
   const statusColor = newTask.status?.color ?? newTask.statusColor;
   const statusAbbreviation = newTask.status?.abbreviation ?? newTask.statusAbbreviation;
   if (statusId != null) result.statusId = statusId;
   if (statusName != null) result.statusName = statusName;
   if (statusColor != null) result.statusColor = statusColor;
   if (statusAbbreviation != null) result.statusAbbreviation = statusAbbreviation;

   return result;
}

/**
 * Builds a complete SchedulerEvent for a newly created assignment from the tearsheet callback.
 * Extends mapNewTaskToSchedulerEvent with identity fields needed by the event/assignment stores.
 */
export function mapNewTaskToFullSchedulerEvent(
   newTask: NewTaskFromTearsheet & { id?: string; resourceName?: string },
): Partial<SchedulerEvent> & { id: string; manuallyScheduled: boolean } {
   const partial = mapNewTaskToSchedulerEvent(newTask);
   return {
      ...partial,
      id: newTask.id ?? "",
      assignmentId: newTask.id ?? "",
      resourceName: newTask.resourceName ?? newTask.name ?? "",
      manuallyScheduled: true,
   };
}

/**
 * Maps a resource-schedule event record to UnassignTaskData for handleUnassignTask.
 * Use when the event was loaded with full assignment fields (categoryId, instructionText, etc.)
 * so no additional API fetch is needed.
 */
export function mapEventRecordToUnassignTaskData(
   eventRecord: EventRecordForUnassign,
): UnassignTaskData {
   const id = String(getEventRecordData(eventRecord, "id") ?? "");
   const startDateRaw = getEventRecordData(eventRecord, "startDate");
   const endDateRaw = getEventRecordData(eventRecord, "endDate");
   const startDate =
      startDateRaw instanceof Date
         ? startDateRaw
         : startDateRaw != null
         ? new Date(getAttachedDate(startDateRaw as number))
         : new Date();
   const endDate =
      endDateRaw instanceof Date
         ? endDateRaw
         : endDateRaw != null
         ? new Date(getAttachedDate(endDateRaw as number))
         : new Date();
   const startTimeRaw = getEventRecordData(eventRecord, "startTime");
   const endTimeRaw = getEventRecordData(eventRecord, "endTime");
   const startTime =
      startTimeRaw == null
         ? null
         : typeof startTimeRaw === "string"
         ? startTimeRaw
         : timeFloatToString(startTimeRaw as number) ?? null;
   const endTime =
      endTimeRaw == null
         ? null
         : typeof endTimeRaw === "string"
         ? endTimeRaw
         : timeFloatToString(endTimeRaw as number) ?? null;
   const projectId = String(getEventRecordData(eventRecord, "projectId") ?? "");
   const workDays = (getEventRecordData(eventRecord, "workDays") as WorkDays) ?? ({} as WorkDays);
   const tagIdsRaw = getEventRecordData(eventRecord, "tagIds");
   const tagIds = Array.isArray(tagIdsRaw) ? (tagIdsRaw as string[]) : [];

   return {
      id,
      startDate,
      endDate,
      startTime,
      endTime,
      jobTitleId: (getEventRecordData(eventRecord, "jobTitleId") as string) ?? null,
      projectId,
      workDays,
      categoryId: (getEventRecordData(eventRecord, "categoryId") as string) ?? null,
      instructionText: (getEventRecordData(eventRecord, "instructionText") as string) ?? null,
      percentAllocated: (getEventRecordData(eventRecord, "percentAllocated") as number) ?? null,
      statusId: (getEventRecordData(eventRecord, "statusId") as string) ?? null,
      subcategoryId: (getEventRecordData(eventRecord, "subcategoryId") as string) ?? null,
      tagIds,
      workScopeText: (getEventRecordData(eventRecord, "workScopeText") as string) ?? null,
   };
}

/**
 * Validates whether an event placement is allowed based on the work schedule.
 * Assignments may overlap non-working days and time-off in the middle of the range.
 * Only the start and end boundaries are checked against the work schedule.
 * @param startDate start of the assignment
 * @param endDate end of the assignment (Bryntum exclusive)
 * @param workDays work schedule of the assignment
 * @returns true if the event placement is valid, false otherwise
 */
export function validateEventPlacement(
   startDate: Date,
   endDate: Date,
   workDays: Record<string, boolean> | null,
): boolean {
   if (!workDays) return true;
   const inclusiveEndDate = new Date(endDate.getTime() - 1);
   return workDays[startDate.getDay()] && workDays[inclusiveEndDate.getDay()];
}

/**
 * Adjusts the event's end date to ensure it does not include the following day.
 * @param eventRecord The event model whose end date will be adjusted.
 */
export const adjustEventEndDate = (eventRecord: EventModel) => {
   const endDate = eventRecord?.endDate;
   if (!(endDate instanceof Date) || Number.isNaN(endDate.getTime())) return;
   eventRecord.endDate = new Date(endDate.getTime() - 1);
};

/**
 * Checks if a given event ID belongs to an equipment resource by looking it up
 * in the scheduler's event store. Falls back to checking the payload's resourceType field.
 */
export type IsEquipmentEventFn = (eventId: string) => boolean;

/**
 * Converts Bryntum payload to one expected by the sync API
 * or does not convert if not a sync, isn't a POST,
 * or has no parse-able body.
 * TODO(WFP-6932): Once sync supports equipment assignments, remove the isEquipmentEvent
 * callback and equipment filtering logic from this function, but keep the resourceId field mapping.
 * @see https://procoretech.atlassian.net/browse/WFP-6932
 * @returns sync API payload
 */
export const createTransformPayloadBeforeSend = (isEquipmentEvent: IsEquipmentEventFn) => {
   return async ({
      requestType,
      requestConfig,
   }: {
      requestType: "sync" | "load";
      requestConfig: any;
   }) => {
      if (
         requestType !== "sync" ||
         String(requestConfig?.method || "").toUpperCase() !== HttpMethod.POST ||
         typeof requestConfig?.body !== "string"
      ) {
         return Promise.resolve();
      }
      let bryntumBody: any;
      try {
         bryntumBody = JSON.parse(requestConfig.body);
      } catch {
         return Promise.resolve();
      }
      if (!bryntumBody || typeof bryntumBody !== "object") return Promise.resolve();

      const assignmentLinkIdPrefix = "assignment-";
      const normalizeAssignmentId = (id: string): string => {
         return id.startsWith(assignmentLinkIdPrefix)
            ? id.slice(assignmentLinkIdPrefix.length)
            : id;
      };
      const equipmentEventIds = new Set<string>();
      const mergedAssignments = new Map<string, any>();
      (bryntumBody?.events?.updated ?? [])
         .filter((event: any) => event.id)
         .forEach((event: any) => {
            if (
               event.resourceType === ResourceType.EQUIPMENT ||
               isEquipmentEvent(String(event.id))
            ) {
               equipmentEventIds.add(event.id);
               return;
            }
            mergedAssignments.set(event.id, {
               ...(mergedAssignments.get(event.id) || {}),
               id: event.id,
               start_day: event?.startDate
                  ? getDetachedDay(toLocalDate(event?.startDate))
                  : undefined,
               end_day: event?.endDate ? getDetachedDay(toLocalDate(event?.endDate)) : undefined,
            });
         });
      (bryntumBody?.assignments?.updated ?? [])
         .filter((assignment: any) => assignment.id && assignment.resourceId)
         .forEach((assignment: any) => {
            const assignmentId = normalizeAssignmentId(assignment.id);
            if (equipmentEventIds.has(assignmentId) || isEquipmentEvent(assignmentId)) return;
            mergedAssignments.set(assignmentId, {
               ...(mergedAssignments.get(assignmentId) || {}),
               id: assignmentId,
               resource_id: assignment.resourceId,
            });
         });

      const updated = Array.from(mergedAssignments.values()).filter(
         (a) => a?.id && Object.keys(a).length > 1,
      );
      const removed = (bryntumBody?.events?.removed ?? [])
         .filter((r: any) => !isEquipmentEvent(String(r.id)))
         .map((r: any) => r.id);

      requestConfig.body = JSON.stringify({
         type: "sync",
         requestId: bryntumBody?.requestId,
         revision: bryntumBody?.revision,
         assignments: {
            added: [],
            updated,
            removed,
         },
      });
      return Promise.resolve();
   };
};
